import {Card} from 'platform/components';
import {Box} from 'platform/foundation';

import {Dispatch, SetStateAction} from 'react';
import {useSelector} from 'react-redux';

import {isNil, isNotNil} from 'ramda';

import {DrillDownLevelName, selectTenant, SourcingVehicleDetailResponseBody} from '@dms/api/core';
import {i18n} from '@dms/i18n';
import {testIds} from '@dms/routes';
import {DrillDown} from '@dms/shared';

import {buildObject} from 'shared/web';

import {useSourcingCurrency} from '../../../../hooks/useSourcingCurrency';
import {useSourcingTargetCountry} from '../../../../hooks/useSourcingTargetCountry';
import {convertMileageToAlphaMileageRange} from '../FullScreenDetailLayout/utils/convertMileageToAlphaMileageRange';

interface DrillDownTabProps {
  vehicle?: SourcingVehicleDetailResponseBody;
  setFullScreen: Dispatch<SetStateAction<boolean>>;
}

export function DrillDownTab(props: DrillDownTabProps) {
  const {data: selectedTenant} = useSelector(selectTenant);
  const tenantCountry = selectedTenant?.country;
  const currency = useSourcingCurrency()?.code;
  const country = useSourcingTargetCountry()?.code ?? tenantCountry;

  if (isNil(props.vehicle) || isNil(country) || isNil(currency)) {
    return null;
  }

  const permanentFilters = buildObject<Partial<Record<DrillDownLevelName, string>>>({})
    .make(props.vehicle.sourcingVehicle.make?.key, isNotNil(props.vehicle.sourcingVehicle.make))
    .model_family_group(
      props.vehicle.sourcingVehicle.modelFamilyGroup?.key,
      isNotNil(props.vehicle.sourcingVehicle.modelFamilyGroup)
    )
    .model(props.vehicle.sourcingVehicle.model?.key, isNotNil(props.vehicle.sourcingVehicle.model))
    .build();

  return (
    <Card
      title={i18n.t('general.labels.drillDown')}
      tooltip={i18n.t('entity.vehicle.labels.drillDownTooltip')}
      actions={[
        {
          type: 'iconButton',
          icon: 'action/zoom_in',
          onClick: () => props.setFullScreen(true),
        },
      ]}
    >
      <Box overflowX="auto">
        <DrillDown
          regions={[country]}
          currency={currency ?? 'CZK'}
          hiddenLevels={['make', 'model_family_group', 'model']}
          permanentFilters={permanentFilters}
          selectedRow={{
            make: props.vehicle.sourcingVehicle.make?.key,
            model_family_group: props.vehicle.sourcingVehicle.modelFamilyGroup?.key,
            model: props.vehicle.sourcingVehicle.model?.key,
            year: props.vehicle.sourcingVehicle.year?.toString(),
            fuel_type: props.vehicle.sourcingVehicle.fuelType?.key,
            drive: props.vehicle.sourcingVehicle.driveType?.key,
            transmission: props.vehicle.sourcingVehicle.transmission?.key,
            power: props.vehicle.sourcingVehicle.power?.toString(),
            mileage_range: convertMileageToAlphaMileageRange(props.vehicle.sourcingVehicle.mileage),
            car_style: props.vehicle.sourcingVehicle.carStyle?.key,
          }}
          hideConfigurator
          data-testid={testIds.sourcing.vehicleDetail('drillDownTab-drillDown')}
        />
      </Box>
    </Card>
  );
}
